(function() {
  'use strict';

  // Centralized device option names across all supported languages
  const DEVICE_OPTION_NAMES = [
    'device', 'devices',
    // German
    'gerät', 'geraet',
    // French
    'appareil', 'dispositif',
    // Spanish/Italian/Portuguese
    'dispositivo',
    // Dutch
    'apparaat', 'toestel',
    // Swedish
    'enhet',
    // Norwegian/Danish
    'enhed',
    // Finnish
    'laite',
    // Greek
    'συσκευή', 'συσκευη',
    // Japanese
    'デバイス', '機種',
    // Korean
    '기기', '장치',
    // Chinese
    '设备', '機型', '型号',
    // Arabic
    'جهاز',
    // Hebrew
    'מכשיר',
    // Hindi
    'डिवाइस', 'उपकरण',
    // Tamil
    'சாதனம்'
  ];

  // Helper to check if an option name is a device option
  function isDeviceOptionName(optName) {
    if (!optName) return false;
    return DEVICE_OPTION_NAMES.includes(optName.toLowerCase().trim());
  }

  // Centralized color option names across all supported languages
  const COLOR_OPTION_NAMES = [
    'color', 'colour', 'colors', 'colours',
    // German
    'farbe',
    // French
    'couleur',
    // Spanish
    'color',
    // Italian
    'colore',
    // Portuguese
    'cor',
    // Dutch
    'kleur',
    // Swedish/Norwegian/Danish
    'färg', 'farge', 'farve',
    // Finnish
    'väri',
    // Greek
    'χρώμα', 'χρωμα',
    // Japanese
    '色', 'カラー',
    // Korean
    '색상', '색깔', '컬러',
    // Chinese
    '颜色', '顏色',
    // Arabic
    'لون',
    // Hebrew
    'צבע',
    // Hindi
    'रंग', 'कलर',
    // Tamil
    'நிறம்'
  ];

  // Helper to check if an option name is a color option
  function isColorOptionName(optName) {
    if (!optName) return false;
    return COLOR_OPTION_NAMES.includes(optName.toLowerCase().trim());
  }

  const BundleDrawer = {
    drawer: null,
    overlay: null,
    currentStep: 1,
    totalSteps: 4,
    selections: {
      device: null,
      material: null,
      products: [],
      variants: {},
      excluded: new Set()
    },
    bundleProducts: [],
    currentProduct: null,
    isLoadingProducts: false,
    
    hydrateFromTemplate() {
      var tpl = document.getElementById('bundle-drawer-tpl');
      if (!tpl) return false;
      tpl.parentNode.insertBefore(document.importNode(tpl.content, true), tpl);
      tpl.remove();
      return true;
    },

    init() {
      this.drawer = document.querySelector('[data-bundle-drawer]');
      if (!this.drawer) {
        this.hydrateFromTemplate();
        this.drawer = document.querySelector('[data-bundle-drawer]');
        if (!this.drawer) return;
      }
      
      this.createOverlay();
      this.bindEvents();
    },
    
    createOverlay() {
      this.overlay = document.createElement('div');
      this.overlay.className = 'bundle-drawer-overlay';
      this.overlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        z-index: 9998;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      `;
      document.body.appendChild(this.overlay);
    },
    
    bindEvents() {
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-action="open-bundle-drawer"]');
        if (openBtn) {
          e.preventDefault();
          // Mark that user has clicked the bundle pill on this page load (in-memory flag)
          this.bundlePillClickedThisPage = true;
          const bundleData = openBtn.dataset.bundleProducts;
          const productHandle = openBtn.dataset.productHandle;
          this.open(bundleData, productHandle);
        }
        
        const minimizeBtn = e.target.closest('[data-action="minimize-bundle-drawer"]');
        if (minimizeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.minimize();
          return;
        }
        
        const closeBtn = e.target.closest('[data-action="close-bundle-drawer"]');
        if (closeBtn || e.target === this.overlay) {
          this.close();
          return;
        }
        
        const nextBtn = e.target.closest('[data-action="bundle-next"]');
        if (nextBtn && !nextBtn.disabled) {
          this.nextStep();
        }
        
        const backBtn = e.target.closest('[data-action="bundle-back"]');
        if (backBtn) {
          this.prevStep();
        }
        
        const deviceOption = e.target.closest('.bundle-device-option');
        if (deviceOption) {
          this.selectDevice(deviceOption);
        }
        
        // Handle zoom swatch click (before material option to prevent selection)
        const zoomBtn = e.target.closest('[data-zoom-swatch]');
        if (zoomBtn) {
          e.preventDefault();
          e.stopPropagation();
          const materialOption = zoomBtn.closest('.bundle-material-option');
          if (materialOption) {
            const imageUrl = materialOption.dataset.swatchImage;
            const caption = materialOption.querySelector('.bundle-material-name')?.textContent || '';
            this.openSwatchLightbox(imageUrl, caption + ' Leather');
          }
          return;
        }
        
        // Handle lightbox close
        const closeLightbox = e.target.closest('[data-close-lightbox]');
        if (closeLightbox) {
          this.closeSwatchLightbox();
          return;
        }
        
        // Handle lightbox zoom
        const zoomIn = e.target.closest('[data-lightbox-zoom-in]');
        if (zoomIn) {
          this.zoomLightboxImage(1.25);
          return;
        }
        
        const zoomOut = e.target.closest('[data-lightbox-zoom-out]');
        if (zoomOut) {
          this.zoomLightboxImage(0.8);
          return;
        }
        
        const materialOption = e.target.closest('.bundle-material-option');
        if (materialOption) {
          this.selectMaterial(materialOption);
        }
        
        const colorSwatch = e.target.closest('.bundle-color-swatch');
        if (colorSwatch) {
          this.selectColor(colorSwatch);
        }
        
        // Handle product image click to open product page in same window
        const productImage = e.target.closest('.bundle-item-card__image');
        if (productImage) {
          const card = productImage.closest('.bundle-item-card');
          const productHandle = card?.dataset.productHandle;
          if (productHandle) {
            let rootUrl = window.bundleRootUrl || '/';
            if (!rootUrl.endsWith('/')) rootUrl += '/';
            window.location.href = `${rootUrl}products/${productHandle}`;
          }
        }
        
        // Handle swatch slider navigation
        const swatchNav = e.target.closest('.bundle-swatches-nav');
        if (swatchNav) {
          e.preventDefault();
          e.stopPropagation();
          const wrapper = swatchNav.closest('.bundle-swatches-wrapper');
          const container = wrapper?.querySelector('.bundle-item-card__colors');
          if (container) {
            const scrollAmount = 100;
            if (swatchNav.classList.contains('bundle-swatches-nav--left')) {
              container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
              container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
            setTimeout(() => this.updateSwatchNavVisibility(wrapper, container), 300);
          }
        }
        
        // Handle floating bundle dismiss button
        const dismissBtn = e.target.closest('[data-dismiss-bundle]');
        if (dismissBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.dismissFloatingButton();
          return;
        }
        
        // Handle perks modal open
        const showPerksBtn = e.target.closest('[data-action="show-perks-modal"]');
        if (showPerksBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.openPerksModal();
          return;
        }
        
        // Handle perks modal close
        const closePerksModal = e.target.closest('[data-close-perks-modal]');
        if (closePerksModal) {
          e.preventDefault();
          this.closePerksModal();
          return;
        }
        
        // Handle load more devices
        const loadMoreBtn = e.target.closest('[data-action="load-more-devices"]');
        if (loadMoreBtn) {
          const moreDevices = document.querySelector('[data-more-devices]');
          const isExpanded = loadMoreBtn.getAttribute('aria-expanded') === 'true';
          loadMoreBtn.setAttribute('aria-expanded', !isExpanded);
          if (moreDevices) {
            moreDevices.style.display = isExpanded ? 'none' : 'grid';
          }
          loadMoreBtn.querySelector('span').textContent = isExpanded ? 
            (window.bundleLoadMoreText || 'Load more models') : 
            (window.bundleShowLessText || 'Show less');
          return;
        }
        
        const excludeBtn = e.target.closest('[data-action="exclude-product"]');
        if (excludeBtn) {
          this.toggleExcludeProduct(excludeBtn.dataset.productId);
          return;
        }
        
        // Handle clicking collapsed card to re-add product
        const collapsedCard = e.target.closest('.bundle-item-card__collapsed');
        if (collapsedCard && !e.target.closest('.bundle-item-card__exclude')) {
          const card = collapsedCard.closest('.bundle-item-card');
          if (card && card.dataset.productId) {
            this.toggleExcludeProduct(card.dataset.productId);
            return;
          }
        }
        
        // Handle skip iPhone case toggle
        const skipCaseCheckbox = e.target.closest('[data-skip-case-checkbox]');
        if (skipCaseCheckbox) {
          this.handleSkipCaseToggle(skipCaseCheckbox.checked);
        }
        
        // Handle apply-to-all color swatch click
        const applyAllSwatch = e.target.closest('.bundle-apply-all__swatch');
        if (applyAllSwatch) {
          const color = applyAllSwatch.dataset.applyColor;
          if (color) {
            this.applyColorToAll(color);
          }
          return;
        }
        
        // Handle apply-to-all navigation
        const applyNav = e.target.closest('[data-apply-nav]');
        if (applyNav) {
          const container = document.querySelector('[data-apply-all-swatches]');
          if (container) {
            const direction = applyNav.dataset.applyNav === 'prev' ? -1 : 1;
            container.scrollBy({ left: direction * 120, behavior: 'smooth' });
            setTimeout(() => this.updateApplyAllNavVisibility(), 300);
          }
          return;
        }
      });
      
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          // Close lightbox first if open
          const lightbox = document.querySelector('[data-swatch-lightbox]');
          if (lightbox && lightbox.getAttribute('aria-hidden') === 'false') {
            this.closeSwatchLightbox();
            return;
          }
          // Then close drawer if open
          if (this.drawer.getAttribute('aria-hidden') === 'false') {
            this.close();
          }
        }
      });
      
      // Handle browser back button - store original page state
      this.originalHistoryLength = history.length;
      this.historyStatesAdded = 0;
      
      window.addEventListener('popstate', (e) => {
        // Only handle if our drawer is open
        if (this.drawer.getAttribute('aria-hidden') === 'false') {
          if (this.currentStep > 1) {
            // Go back a step
            this.currentStep--;
            this.updateStepDisplay();
            this.updateProgress();
            this.updateNavButtons();
            this.historyStatesAdded--;
            // Push new state to prevent page navigation
            if (this.historyStatesAdded >= 0) {
              history.pushState({ bundleDrawer: true, step: this.currentStep }, '');
            }
          } else {
            // On step 1, close the drawer without navigating away
            this.closeWithoutHistoryPop();
          }
        }
      });
    },
    
    closeWithoutHistoryPop() {
      // Close drawer without triggering history back
      if (this.selections.device || this.selections.material || Object.keys(this.selections.variants).length > 0) {
        this.saveBundleProgress();
      }
      
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.style.opacity = '0';
      this.overlay.style.visibility = 'hidden';
      this.overlay.style.pointerEvents = 'none';
      document.body.style.overflow = '';
      document.body.classList.remove('bundle-drawer-open', 'has-overlay');
      this.historyStatesAdded = 0;
      
      const saved = sessionStorage.getItem('bundleProgress');
      if (saved && !this.floatingButtonDismissed) {
        this.showFloatingBundleButton();
      }
    },
    
    async open(bundleData, productHandle) {
      try {
        if (bundleData) {
          this.bundleProducts = typeof bundleData === 'string' ? JSON.parse(bundleData) : bundleData;
        }
        
        this.currentProductHandle = productHandle;
        this.reset();
        this.populateDeviceOptions();
        this.drawer.setAttribute('aria-hidden', 'false');
        this.overlay.style.opacity = '1';
        this.overlay.style.visibility = 'visible';
        this.overlay.style.pointerEvents = 'auto';
        document.body.style.overflow = 'hidden';
        
        // Push history state for back button handling
        history.pushState({ bundleDrawer: true, step: 1 }, '');
        this.historyStatesAdded = 1;
        
        await this.loadBundleProducts();
        this.autoSelectMaterial();
        document.body.classList.add('bundle-drawer-open');
      } catch (err) {
        console.error('Error opening bundle drawer:', err);
      }
    },
    
    populateDeviceOptions() {
      const deviceGrid = document.querySelector('[data-device-options]');
      const moreDevicesGrid = document.querySelector('[data-more-devices]');
      const loadMoreBtn = document.querySelector('[data-action="load-more-devices"]');
      
      if (!deviceGrid) return;
      
      // Clear existing options and reset load-more state
      deviceGrid.innerHTML = '';
      if (moreDevicesGrid) {
        moreDevicesGrid.innerHTML = '';
        moreDevicesGrid.style.display = 'none';
      }
      if (loadMoreBtn) {
        loadMoreBtn.setAttribute('aria-expanded', 'false');
        const btnText = loadMoreBtn.querySelector('span');
        if (btnText) btnText.textContent = window.bundleLoadMoreText || 'Load more models';
      }
      
      let devices = [];
      
      // Priority 1: Extract unique device names from product variants (most reliable)
      if (window.productJSON && window.productJSON.variants && window.productJSON.options) {
        // Find which option index is "Device" (supports localized option names)
        const deviceOptionIndex = window.productJSON.options.findIndex(opt => {
          const optName = typeof opt === 'string' ? opt : (opt.name || '');
          return isDeviceOptionName(optName);
        });
        
        if (deviceOptionIndex !== -1) {
          const uniqueDevices = new Set();
          window.productJSON.variants.forEach(variant => {
            // Get device value from variant options (option1, option2, option3)
            const deviceValue = variant[`option${deviceOptionIndex + 1}`];
            if (deviceValue && typeof deviceValue === 'string') {
              uniqueDevices.add(deviceValue);
            }
          });
          devices = Array.from(uniqueDevices);
        }
      }
      
      // Priority 2: Try options_with_values for structured data (supports localized option names)
      if (devices.length === 0 && window.productJSON?.options_with_values) {
        const deviceOption = window.productJSON.options_with_values.find(opt => 
          isDeviceOptionName(opt.name)
        );
        if (deviceOption && deviceOption.values) {
          devices = deviceOption.values.map(v => typeof v === 'string' ? v : v.value || v.title);
        }
      }
      
      // Priority 3: Try swatch radio inputs with data-option-value
      if (devices.length === 0) {
        const swatchInputs = document.querySelectorAll('[data-option-position="1"] input[type="radio"], .SizeSwatch__Radio[name="option1"]');
        if (swatchInputs.length > 0) {
          const uniqueDevices = new Set();
          swatchInputs.forEach(input => {
            const val = input.dataset.optionValue || input.value;
            if (val && !/^\d+$/.test(val) && !val.includes('/') && !val.includes('$')) {
              uniqueDevices.add(val);
            }
          });
          devices = Array.from(uniqueDevices);
        }
      }
      
      // Fallback to common iPhone models if no devices found
      if (devices.length === 0) {
        /* Last resort only — reached when a product exposes no device option
           at all. Kept in step with what the store actually sells: the 18
           series through the 14 Pro Max. Nothing at or below the 13 is stocked,
           so offering it here sent shoppers to models that do not exist. */
        devices = [
          'iPhone 18 Pro Max', 'iPhone 18 Pro', 'iPhone 18',
          'iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 17 Air', 'iPhone 17',
          'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16',
          'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15',
          'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14'
        ];
      }
      
      console.log('Bundle Drawer - Devices found:', devices.length, devices.slice(0, 5));
      
      // Determine how many to show initially (first 8)
      const initialCount = 8;
      const initialDevices = devices.slice(0, initialCount);
      const moreDevicesList = devices.slice(initialCount);
      
      // Create device buttons for initial display
      initialDevices.forEach(device => {
        const btn = this.createDeviceButton(device);
        deviceGrid.appendChild(btn);
      });
      
      // Create device buttons for "load more" section
      if (moreDevicesList.length > 0 && moreDevicesGrid && loadMoreBtn) {
        moreDevicesList.forEach(device => {
          const btn = this.createDeviceButton(device);
          moreDevicesGrid.appendChild(btn);
        });
        loadMoreBtn.style.display = 'flex';
      } else if (loadMoreBtn) {
        loadMoreBtn.style.display = 'none';
      }
      
      // Sync skip case toggle UI with stored state
      this.syncSkipCaseUI();
    },
    
    createDeviceButton(deviceName) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-device-option';
      btn.dataset.device = deviceName;
      btn.dataset.testid = `device-${deviceName.toLowerCase().replace(/\s+/g, '-')}`;
      
      const nameSpan = document.createElement('span');
      nameSpan.className = 'bundle-device-name';
      nameSpan.textContent = deviceName;
      
      btn.appendChild(nameSpan);
      return btn;
    },
    
    autoSelectMaterial() {
      const handle = this.currentProductHandle || '';
      const materials = ['crocodile', 'croco', 'lizard', 'saffiano', 'wave', 'pebble'];
      let selectedMaterial = null;
      
      for (const material of materials) {
        if (handle.toLowerCase().includes(material)) {
          selectedMaterial = material === 'croco' ? 'crocodile' : material;
          break;
        }
      }
      
      if (selectedMaterial) {
        const grid = document.querySelector('[data-material-options]');
        const selectedOption = grid?.querySelector(`.bundle-material-option[data-material="${selectedMaterial}"]`);
        
        if (selectedOption && grid) {
          // Mark as selected
          selectedOption.classList.add('selected');
          selectedOption.classList.add('bundle-material-option--highlighted');
          this.selections.material = selectedMaterial;
          
          // Move selected option to top
          grid.insertBefore(selectedOption, grid.firstChild);
          
          this.updateNavButtons();
        }
      }
    },
    
    close() {
      // Save progress before closing so floating button can be shown
      const hasProgress = this.selections.device || this.selections.material || Object.keys(this.selections.variants).length > 0;
      if (hasProgress) {
        this.saveBundleProgress();
      }
      
      // Blur any focused element inside the drawer to prevent aria-hidden focus warning
      const focusedEl = this.drawer.querySelector(':focus');
      if (focusedEl) focusedEl.blur();
      
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.style.opacity = '0';
      this.overlay.style.visibility = 'hidden';
      this.overlay.style.pointerEvents = 'none';
      document.body.style.overflow = '';
      document.body.classList.remove('bundle-drawer-open', 'has-overlay');
      
      // Always show floating button when drawer is closed (user showed interest by opening)
      // Use setTimeout to ensure CSS class is fully removed before showing button
      setTimeout(() => this.showFloatingBundleButton(), 10);
    },
    
    reset() {
      this.currentStep = 1;
      this.selections = {
        device: null,
        material: null,
        products: [],
        variants: {},
        excluded: new Set(),
        skipIphoneCase: false
      };
      this.currentProduct = null;
      this.updateStepDisplay();
      this.updateProgress();
      this.updateNavButtons();
      
      // Reset device and material selections including highlighted state
      document.querySelectorAll('.bundle-device-option, .bundle-material-option').forEach(el => {
        el.classList.remove('selected');
        el.classList.remove('bundle-material-option--highlighted');
      });
    },
    
    async loadBundleProducts() {
      // Prevent double loading
      if (this.isLoadingProducts) {
        console.log('[Bundle] loadBundleProducts skipped - already loading');
        return;
      }
      this.isLoadingProducts = true;
      
      // Clear products array
      this.selections.products = [];
      const loadedIds = new Set();
      
      // Get locale-aware root URL (e.g., "/ja" for Japanese, "" for default)
      const rootUrl = (window.bundleRootUrl || '').replace(/\/$/, '');
      
      // First, add the current product (the one user is viewing)
      if (this.currentProductHandle) {
        try {
          // Use redirect: 'follow' to handle localized handles that may redirect
          const response = await fetch(`${rootUrl}/products/${this.currentProductHandle}.js`, { redirect: 'follow' });
          if (response.ok) {
            const product = await response.json();
            product.isMainProduct = true;
            this.currentProduct = product;
            if (!loadedIds.has(String(product.id))) {
              loadedIds.add(String(product.id));
              this.selections.products.push(product);
            }
          }
        } catch (err) {
          console.error(`Error loading current product:`, err);
        }
      }
      
      // Then add bundle products from metafield (deduplicated)
      if (this.bundleProducts && this.bundleProducts.length) {
        const uniqueHandles = [...new Set(this.bundleProducts)];
        for (const handle of uniqueHandles) {
          if (handle === this.currentProductHandle) continue;
          
          try {
            // Use redirect: 'follow' to handle localized handles that may redirect
            const response = await fetch(`${rootUrl}/products/${handle}.js`, { redirect: 'follow' });
            if (response.ok) {
              const product = await response.json();
              if (!loadedIds.has(String(product.id))) {
                loadedIds.add(String(product.id));
                this.selections.products.push(product);
              }
            }
          } catch (err) {
            console.error(`Error loading product ${handle}:`, err);
          }
        }
      }
      
      this.isLoadingProducts = false;
      console.log('[Bundle] Products loaded:', this.selections.products.length, 'unique products');
    },
    
    selectDevice(element) {
      document.querySelectorAll('.bundle-device-option').forEach(el => {
        el.classList.remove('selected');
      });
      element.classList.add('selected');
      const newDevice = element.dataset.device;
      const deviceChanged = this.selections.device !== newDevice;
      this.selections.device = newDevice;
      
      // Turn off skip case toggle when a device is explicitly selected
      if (this.selections.skipIphoneCase) {
        this.selections.skipIphoneCase = false;
        this.syncSkipCaseUI();
      }
      
      // If device changed and we have existing variant selections, clear them
      // so they can be re-selected with the correct device option
      if (deviceChanged && this.selections.variants && Object.keys(this.selections.variants).length > 0) {
        this.selections.variants = {};
        // Also trigger a re-render of step 3 if products are loaded
        // This ensures the iPhone case appears when user goes back and selects a device
        if (this.selections.products && this.selections.products.length > 0) {
          this.renderProductCards();
        }
      }
      
      this.saveBundleProgress();
      this.updateNavButtons();
      
      // Auto-advance to next step after device selection
      setTimeout(() => {
        this.nextStep();
      }, 200);
    },
    
    async selectMaterial(element) {
      // Prevent multiple clicks while loading
      if (this.isMaterialLoading) return;
      
      // Only remove 'selected' class, keep 'highlighted' on the auto-detected material
      document.querySelectorAll('.bundle-material-option').forEach(el => {
        el.classList.remove('selected');
      });
      element.classList.add('selected');
      
      const newMaterial = element.dataset.material;
      const previousMaterial = this.selections.material;
      this.selections.material = newMaterial;
      this.updateNavButtons();
      
      // Show loading state on material grid
      this.isMaterialLoading = true;
      const materialGrid = document.querySelector('[data-material-options]');
      if (materialGrid) materialGrid.classList.add('bundle-material-grid--loading');
      
      // Always reload products when a material is clicked (even if same material)
      // This ensures products load properly when navigating back and re-selecting
      await this.reloadProductsForMaterial(newMaterial);
      
      // Remove loading state
      this.isMaterialLoading = false;
      if (materialGrid) materialGrid.classList.remove('bundle-material-grid--loading');
      
      // Auto-advance to next step if device is selected OR if skip case is enabled
      // (skip case means we came from step 1 without explicitly selecting a device)
      if (this.selections.device || this.selections.skipIphoneCase) {
        setTimeout(() => {
          this.nextStep();
        }, 200);
      }
    },
    
    async reloadProductsForMaterial(newMaterial) {
      // Support both tag formats: bundle-{material} and {material}-bundle
      const materialTags = [`bundle-${newMaterial}`, `${newMaterial}-bundle`];
      
      console.log(`[Bundle] Loading products with tags: ${materialTags.join(' OR ')}`);
      
      // Clear variant selections and excluded products since products are changing
      this.selections.variants = {};
      this.selections.excluded = new Set();
      
      // Reload products using tag filter (reset loading flag first)
      this.isLoadingProducts = false;
      await this.loadProductsByTags(materialTags);
      
      // Re-render step 3 if we're on it
      if (this.currentStep === 3) {
        this.renderProductCards();
      }
    },
    
    async loadProductsByTags(tags) {
      if (this.isLoadingProducts) return;
      this.isLoadingProducts = true;
      
      // Clear products array
      this.selections.products = [];
      
      // Get locale-aware root URL
      const rootUrl = (window.bundleRootUrl || '').replace(/\/$/, '');
      
      // Normalize tags array to lowercase
      const normalizedTags = tags.map(t => t.toLowerCase());
      
      // Fetch all products and filter by any of the tags
      try {
        const response = await fetch(`${rootUrl}/products.json?limit=250`);
        if (response.ok) {
          const data = await response.json();
          // Filter products that have any of the matching tags
          // Tags can be an array or comma-separated string depending on API version
          const matchingProducts = data.products.filter(p => {
            if (!p.tags) return false;
            const productTags = Array.isArray(p.tags) ? p.tags : p.tags.split(', ');
            return productTags.some(pt => normalizedTags.includes(pt.trim().toLowerCase()));
          });
          
          console.log(`[Bundle] Found ${matchingProducts.length} products with tags [${normalizedTags.join(', ')}], fetching full details...`);
          
          // Fetch each product individually to get full variant data (including options array)
          const loadedIds = new Set();
          for (const p of matchingProducts) {
            try {
              // Use redirect: 'follow' to handle localized handles that may redirect
              const productResponse = await fetch(`${rootUrl}/products/${p.handle}.js`, { redirect: 'follow' });
              if (productResponse.ok) {
                const fullProduct = await productResponse.json();
                if (!loadedIds.has(String(fullProduct.id))) {
                  loadedIds.add(String(fullProduct.id));
                  this.selections.products.push(fullProduct);
                }
              }
            } catch (err) {
              console.error(`[Bundle] Error loading product ${p.handle}:`, err);
            }
          }
          
          console.log(`[Bundle] Loaded ${this.selections.products.length} products with full details`);
        }
      } catch (err) {
        console.error('[Bundle] Error fetching products:', err);
      }
      
      this.isLoadingProducts = false;
    },
    
    openSwatchLightbox(imageUrl, caption) {
      const lightbox = document.querySelector('[data-swatch-lightbox]');
      if (!lightbox) return;
      
      const img = lightbox.querySelector('.bundle-swatch-lightbox__image');
      const captionEl = lightbox.querySelector('.bundle-swatch-lightbox__caption');
      
      if (img) img.src = imageUrl;
      if (captionEl) captionEl.textContent = caption;
      
      lightbox.setAttribute('aria-hidden', 'false');
    },
    
    closeSwatchLightbox() {
      const lightbox = document.querySelector('[data-swatch-lightbox]');
      if (lightbox) {
        lightbox.setAttribute('aria-hidden', 'true');
        // Reset zoom level
        const img = lightbox.querySelector('[data-lightbox-image]');
        if (img) img.style.transform = '';
        this.lightboxZoom = 1;
      }
    },
    
    openPerksModal() {
      const modal = document.querySelector('[data-perks-modal]');
      if (modal) {
        modal.setAttribute('aria-hidden', 'false');
      }
    },
    
    closePerksModal() {
      const modal = document.querySelector('[data-perks-modal]');
      if (modal) {
        modal.setAttribute('aria-hidden', 'true');
      }
    },
    
    zoomLightboxImage(factor) {
      const lightbox = document.querySelector('[data-swatch-lightbox]');
      if (!lightbox) return;
      
      const img = lightbox.querySelector('[data-lightbox-image]');
      if (!img) return;
      
      this.lightboxZoom = (this.lightboxZoom || 1) * factor;
      this.lightboxZoom = Math.max(0.5, Math.min(3, this.lightboxZoom)); // Clamp between 0.5x and 3x
      img.style.transform = `scale(${this.lightboxZoom})`;
      img.style.transition = 'transform 0.2s ease';
    },
    
    minimize() {
      console.log('[Bundle] minimize() called');
      this.saveBundleProgress();
      
      // Blur any focused element inside the drawer to prevent aria-hidden focus warning
      const focusedEl = this.drawer.querySelector(':focus');
      if (focusedEl) focusedEl.blur();
      
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.style.opacity = '0';
      this.overlay.style.visibility = 'hidden';
      this.overlay.style.pointerEvents = 'none';
      document.body.style.overflow = '';
      document.body.classList.remove('bundle-drawer-open', 'has-overlay');
      
      // Use setTimeout to ensure CSS class is fully removed before showing button
      console.log('[Bundle] About to show floating button');
      setTimeout(() => this.showFloatingBundleButton(), 10);
    },
    
    hasDeviceOption(product) {
      if (!product || !product.options) return false;
      // Uses centralized DEVICE_OPTION_NAMES via isDeviceOptionName helper
      return product.options.some(opt => {
        const optName = typeof opt === 'string' ? opt : (opt.name || '');
        return isDeviceOptionName(optName);
      });
    },
    
    getProductById(productId) {
      return this.selections.products.find(p => String(p.id) === String(productId));
    },
    
    selectColor(swatch) {
      const card = swatch.closest('.bundle-item-card');
      const productId = card.dataset.productId;
      const product = this.getProductById(productId);
      const hasDevice = product ? this.hasDeviceOption(product) : false;
      
      card.querySelectorAll('.bundle-color-swatch').forEach(el => {
        el.classList.remove('selected');
      });
      swatch.classList.add('selected');
      
      const variantTitle = hasDevice && this.selections.device 
        ? `${this.selections.device} / ${swatch.dataset.color}`
        : swatch.dataset.color;
      
      this.selections.variants[productId] = {
        variantId: swatch.dataset.variantId,
        color: swatch.dataset.color,
        image: swatch.dataset.image,
        price: swatch.dataset.price,
        title: variantTitle,
        hasDevice: hasDevice
      };
      
      const img = card.querySelector('.bundle-item-card__image');
      if (img && swatch.dataset.image) {
        img.src = swatch.dataset.image;
      }
      
      const variantText = card.querySelector('.bundle-item-card__variant');
      if (variantText) {
        // Apply color-based background IMMEDIATELY (disable transition first)
        const colorHex = this.getColorHex(swatch.dataset.color);
        variantText.style.transition = 'none';
        variantText.style.backgroundColor = colorHex;
        variantText.textContent = variantTitle;
        
        // Re-enable transition after a brief delay and trigger animation
        requestAnimationFrame(() => {
          variantText.style.transition = '';
          variantText.classList.remove('animate-selection');
          void variantText.offsetWidth; // Trigger reflow
          variantText.classList.add('animate-selection');
        });
      }
      
      this.updateNavButtons();
      this.saveBundleProgress();
    },
    
    toggleExcludeProduct(productId) {
      const id = String(productId);
      if (this.selections.excluded.has(id)) {
        // Re-adding: clear stored selection so user must select again
        this.selections.excluded.delete(id);
        delete this.selections.variants[id];
      } else {
        // Excluding: keep variant data in case user re-adds, but it will be cleared above
        this.selections.excluded.add(id);
      }
      this.renderProductCards();
      this.initSwatchSliders();
      this.renderApplyAllSwatches();
      this.updateApplyAllSelection();
      this.updateNavButtons();
      this.saveBundleProgress();
    },
    
    isExcluded(productId) {
      return this.selections.excluded.has(String(productId));
    },
    
    handleSkipCaseToggle(skip) {
      this.selections.skipIphoneCase = skip;
      // Update the switch aria-checked state
      const switchEl = document.querySelector('.bundle-skip-case__switch');
      if (switchEl) switchEl.setAttribute('aria-checked', skip ? 'true' : 'false');
      
      // When skip is enabled, remove iPhone case variants from selections
      if (skip) {
        const products = this.selections.products || [];
        products.forEach(product => {
          if (this.isIphoneCaseProduct(product)) {
            delete this.selections.variants[product.id];
          }
        });
        
        // Auto-advance to step 2 (materials) if on step 1
        if (this.currentStep === 1) {
          this.saveBundleProgress();
          // Use setTimeout to let the toggle animation complete, then use nextStep() 
          // so that the full step transition logic runs (event wiring, etc.)
          setTimeout(() => {
            this.nextStep();
          }, 150);
          return;
        }
      }
      // Note: When skip is disabled, case products will be shown again in UI
      // but user must re-select colors manually (variants were deleted when skip was enabled)
      
      this.saveBundleProgress();
      
      // Re-render products/swatches/summary based on current step
      if (this.currentStep >= 3) {
        this.renderProductCards();
        this.renderApplyAllSwatches();
        this.initSwatchSliders();
        if (this.currentStep === 4) {
          this.renderSummary();
        }
      }
      this.updateNavButtons();
    },
    
    syncSkipCaseUI() {
      const skipCaseCheckbox = document.querySelector('[data-skip-case-checkbox]');
      const switchEl = document.querySelector('.bundle-skip-case__switch');
      if (skipCaseCheckbox) {
        skipCaseCheckbox.checked = this.selections.skipIphoneCase || false;
      }
      if (switchEl) {
        switchEl.setAttribute('aria-checked', this.selections.skipIphoneCase ? 'true' : 'false');
      }
    },
    
    isIphoneCaseProduct(product) {
      const handle = product.handle?.toLowerCase() || '';
      const title = product.title?.toLowerCase() || '';
      // Match various locale versions of "iphone case"
      const casePatterns = ['iphone-case', 'iphone case', 'funda-iphone', 'funda iphone', 'coque-iphone', 'coque iphone', 'iphone-hülle', 'iphone hülle', 'custodia-iphone', 'custodia iphone'];
      return casePatterns.some(pattern => handle.includes(pattern) || title.includes(pattern));
    },
    
    nextStep() {
      // Debounce to prevent double-clicking from skipping steps
      const now = Date.now();
      if (this._lastStepChange && now - this._lastStepChange < 400) {
        return;
      }
      this._lastStepChange = now;
      
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
        
        if (this.currentStep === 3) {
          this.renderProductCards();
          this.initSwatchSliders();
          this.renderApplyAllSwatches();
        }
        
        if (this.currentStep === 4) {
          this.renderSummary();
        }
        
        this.updateStepDisplay();
        this.updateProgress();
        this.updateNavButtons();
        
        // Push history state for back button handling
        history.pushState({ bundleDrawer: true, step: this.currentStep }, '');
        this.historyStatesAdded++;
      } else {
        this.addBundleToCart();
      }
    },
    
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.updateStepDisplay();
        this.updateProgress();
        this.updateNavButtons();
      }
    },
    
    updateStepDisplay() {
      document.querySelectorAll('.bundle-step').forEach(step => {
        step.classList.remove('active');
      });
      
      const currentStepEl = document.querySelector(`[data-step="${this.currentStep}"]`);
      if (currentStepEl) {
        currentStepEl.classList.add('active');
      }
      
      const stepIndicator = document.querySelector('[data-current-step]');
      if (stepIndicator) {
        stepIndicator.textContent = this.currentStep;
      }
      
      // Ensure highlighted material (matching product page) is always at top when viewing step 2
      if (this.currentStep === 2) {
        const grid = document.querySelector('[data-material-options]');
        const highlightedMaterial = document.querySelector('.bundle-material-option--highlighted');
        if (grid && highlightedMaterial && highlightedMaterial !== grid.firstElementChild) {
          grid.insertBefore(highlightedMaterial, grid.firstChild);
        }
      }
      
      // Sync skip case toggle UI when on step 1
      if (this.currentStep === 1) {
        this.syncSkipCaseUI();
      }
    },
    
    updateProgress() {
      const progressBar = document.querySelector('[data-progress-bar]');
      if (progressBar) {
        const progress = (this.currentStep / this.totalSteps) * 100;
        progressBar.style.width = `${progress}%`;
      }
    },
    
    updateNavButtons() {
      const backBtn = document.querySelector('[data-action="bundle-back"]');
      const nextBtn = document.querySelector('[data-action="bundle-next"]');
      const nextText = nextBtn?.querySelector('[data-next-text]');
      
      if (backBtn) {
        backBtn.style.display = this.currentStep > 1 ? 'flex' : 'none';
      }
      
      let canProceed = false;
      
      switch (this.currentStep) {
        case 1:
          canProceed = !!this.selections.device;
          break;
        case 2:
          canProceed = !!this.selections.material;
          break;
        case 3:
          const activeProducts = this.selections.products.filter(p => {
            if (this.isExcluded(p.id)) return false;
            // Exclude iPhone case products if skip toggle is enabled
            if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(p)) return false;
            return true;
          });
          const hasActiveProducts = activeProducts.length > 0;
          // Check which products are available/unavailable with current apply-all selection
          const allSelected = activeProducts.every(p => {
            // Check if product is unavailable in the applied color
            if (this.appliedAllColor) {
              const matchingVariants = this.getMatchingVariants(p);
              const hasColor = matchingVariants.some(v => {
                const variantColor = v.option2 || v.option1;
                // Use colorsMatch for proper localized color comparison
                return this.colorsMatch(variantColor, this.appliedAllColor);
              });
              // Unavailable products don't block proceeding - they just won't be in the bundle
              if (!hasColor) return true;
            }
            return this.selections.variants[p.id]?.variantId;
          });
          canProceed = hasActiveProducts && allSelected;
          break;
        case 4:
          canProceed = true;
          break;
      }
      
      if (nextBtn) {
        nextBtn.disabled = !canProceed;
      }
      
      if (nextText) {
        if (this.currentStep === this.totalSteps) {
          nextText.textContent = window.bundleTranslations?.addBundle || 'Add Bundle to Cart';
        } else {
          nextText.textContent = window.bundleTranslations?.continue || 'Continue';
        }
      }
    },
    
    renderProductCards() {
      const container = document.querySelector('[data-bundle-items]');
      if (!container) return;
      
      container.innerHTML = '';
      const t = window.bundleTranslations || {};
      
      for (const product of this.selections.products) {
        // Skip iPhone case products if user toggled skip case option
        if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(product)) {
          continue;
        }
        
        const matchingVariants = this.getMatchingVariants(product);
        const isExcluded = this.isExcluded(product.id);
        
        if (matchingVariants.length === 0) continue;
        
        // Skip products that don't have the apply-all color when one is selected
        if (this.appliedAllColor && !isExcluded) {
          const hasColor = matchingVariants.some(v => {
            const variantColor = v.option2 || v.option1;
            return this.colorsMatch(variantColor, this.appliedAllColor);
          });
          if (!hasColor) continue; // Skip rendering this product entirely
        }
        
        const card = document.createElement('div');
        let cardClasses = 'bundle-item-card';
        if (isExcluded) cardClasses += ' bundle-item-card--excluded';
        card.className = cardClasses;
        card.dataset.productId = product.id;
        card.dataset.productHandle = product.handle;
        
        // Use previously selected variant image if exists, otherwise first variant
        const existingSelection = this.selections.variants[product.id];
        const firstVariant = matchingVariants[0];
        const image = existingSelection?.image || firstVariant.featured_image?.src || product.featured_image;
        
        const excludeBtnLabel = isExcluded 
          ? (t.addToBundle || 'Add to bundle') 
          : (t.removeFromBundle || 'Remove from bundle');
        
        // Better SVG icons for exclude/readd
        const excludeIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
        const readdIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>`;
        const excludeBtnIcon = isExcluded ? readdIcon : excludeIcon;
        
        const hasDevice = this.hasDeviceOption(product);
        const initialVariantLabel = hasDevice && this.selections.device 
          ? this.selections.device 
          : (t.selectColor || 'Select color');
        
        // Collapsed card when excluded - show title, color swatch, and readd button
        if (isExcluded) {
          // Get current color selection for this excluded product
          const excludedSelection = this.selections.variants[product.id];
          const excludedColor = excludedSelection?.color || null;
          const excludedColorHex = excludedColor ? this.getColorHex(excludedColor) : '#ccc';
          const colorSwatchHtml = excludedColor 
            ? `<span class="bundle-item-card__collapsed-swatch" style="background-color: ${excludedColorHex}" title="${excludedColor}"></span>`
            : '';
          
          card.innerHTML = `
            <div class="bundle-item-card__collapsed">
              ${colorSwatchHtml}
              <h4 class="bundle-item-card__title">${product.title}</h4>
              <button type="button" 
                class="bundle-item-card__exclude bundle-item-card__exclude--readd" 
                data-action="exclude-product" 
                data-product-id="${product.id}"
                title="${excludeBtnLabel}"
                aria-label="${excludeBtnLabel}">
                ${excludeBtnIcon}
              </button>
            </div>
          `;
        } else {
          // Build product URL with locale prefix
          const rootUrl = (window.bundleRootUrl || '').replace(/\/$/, '');
          const productUrl = `${rootUrl}/products/${product.handle}`;
          
          card.innerHTML = `
            <button type="button" 
              class="bundle-item-card__exclude" 
              data-action="exclude-product" 
              data-product-id="${product.id}"
              title="${excludeBtnLabel}"
              aria-label="${excludeBtnLabel}">
              ${excludeBtnIcon}
            </button>
            <div class="bundle-item-card__top">
              <a href="${productUrl}" class="bundle-item-card__image-link">
                <img class="bundle-item-card__image" src="${this.resizeImage(image, 160)}" alt="${product.title}" loading="lazy">
              </a>
              <div class="bundle-item-card__info">
                <a href="${productUrl}" class="bundle-item-card__title-link">
                  <h4 class="bundle-item-card__title">${product.title}</h4>
                </a>
                <p class="bundle-item-card__variant">${initialVariantLabel}</p>
              </div>
            </div>
            <div class="bundle-swatches-wrapper" data-swatches-wrapper>
              <button type="button" class="bundle-swatches-nav bundle-swatches-nav--left" aria-label="${t.previousColors || 'Previous colors'}" data-testid="button-swatch-prev-${product.id}">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div class="bundle-item-card__colors">
                ${matchingVariants.map(v => this.renderColorSwatch(v, product)).join('')}
              </div>
              <button type="button" class="bundle-swatches-nav bundle-swatches-nav--right" aria-label="${t.moreColors || 'More colors'}" data-testid="button-swatch-next-${product.id}">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          `;
        }
        
        container.appendChild(card);
        
        // Auto-select first variant if not excluded and no previous selection
        if (!isExcluded && !existingSelection) {
          const firstSwatch = card.querySelector('.bundle-color-swatch');
          if (firstSwatch) {
            firstSwatch.click();
          }
        } else if (!isExcluded && existingSelection) {
          // Reapply saved selection UI
          this.applyVariantSelectionUI(card, existingSelection);
        }
      }
    },
    
    applyVariantSelectionUI(card, selection) {
      // Mark correct swatch as selected
      const swatches = card.querySelectorAll('.bundle-color-swatch');
      swatches.forEach(s => {
        s.classList.toggle('selected', s.dataset.variantId === String(selection.variantId));
      });
      
      // Update variant text
      const variantEl = card.querySelector('.bundle-item-card__variant');
      if (variantEl) {
        variantEl.textContent = selection.title || selection.color || '';
        variantEl.classList.add('bundle-item-card__variant--updated');
        variantEl.classList.add('animate-selection');
        
        // Apply color background using the same method as selectColor
        if (selection.color) {
          const colorHex = this.getColorHex(selection.color);
          variantEl.style.backgroundColor = colorHex;
        }
      }
      
      // Update image if saved
      if (selection.image) {
        const img = card.querySelector('.bundle-item-card__image');
        if (img) img.src = selection.image;
      }
    },
    
    initSwatchSliders() {
      const wrappers = document.querySelectorAll('.bundle-swatches-wrapper');
      wrappers.forEach(wrapper => {
        const container = wrapper.querySelector('.bundle-item-card__colors');
        if (container) {
          this.updateSwatchNavVisibility(wrapper, container);
          container.addEventListener('scroll', () => {
            this.updateSwatchNavVisibility(wrapper, container);
          });
        }
      });
    },
    
    renderApplyAllSwatches() {
      const container = document.querySelector('[data-apply-all-swatches]');
      if (!container) return;
      
      // Use loaded product objects (not handles) from selections.products
      const products = this.selections.products || [];
      if (!products.length) return;
      
      // Collect colors from ALL products (including excluded) so swatches don't disappear
      // when user excludes products. Excluded products just won't receive the color.
      const colorMap = new Map(); // normalized -> first original color name
      
      products.forEach(product => {
        // Skip iPhone case products if skip toggle is enabled
        if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(product)) return;
        
        const variants = this.getMatchingVariants(product);
        variants.forEach(v => {
          const color = v.option2 || v.option1;
          if (color) {
            const normalized = this.normalizeColorName(color);
            if (!colorMap.has(normalized)) {
              // Use translated color label from locale, fallback to original variant color
              const colorLabels = window.bundleTranslations?.colorLabels || {};
              const translatedColor = colorLabels[normalized] || color;
              colorMap.set(normalized, translatedColor);
            }
          }
        });
      });
      
      // Convert to array of localized colors (uses translations from bundleTranslations.colorLabels)
      const colors = Array.from(colorMap.values());
      const isSelected = (c) => this.colorsMatch(this.appliedAllColor, c);
      
      container.innerHTML = colors.map(color => {
        const colorHex = this.getColorHex(color);
        const selectedClass = isSelected(color) ? ' selected' : '';
        return `
          <button 
            type="button" 
            class="bundle-apply-all__swatch${selectedClass}"
            data-apply-color="${color}"
            style="background-color: ${colorHex}"
            title="${color}">
          </button>
        `;
      }).join('');
      
      // Update nav visibility
      this.updateApplyAllNavVisibility();
    },
    
    updateApplyAllNavVisibility() {
      const wrapper = document.querySelector('[data-apply-all-colors]');
      const container = document.querySelector('[data-apply-all-swatches]');
      if (!wrapper || !container) return;
      
      const hasOverflow = container.scrollWidth > container.clientWidth + 2;
      const atStart = container.scrollLeft < 5;
      const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;
      
      const leftNav = wrapper.querySelector('[data-apply-nav="prev"]');
      const rightNav = wrapper.querySelector('[data-apply-nav="next"]');
      
      if (leftNav) leftNav.style.opacity = atStart ? '0.3' : '1';
      if (rightNav) rightNav.style.opacity = atEnd ? '0.3' : '1';
    },
    
    applyColorToAll(color) {
      // Toggle off if same color clicked again (use flexible matching)
      if (this.colorsMatch(this.appliedAllColor, color)) {
        this.appliedAllColor = null;
        // Re-render to show all products again
        this.renderProductCards();
        this.initSwatchSliders();
        this.renderApplyAllSwatches();
        this.updateApplyAllSelection();
        this.updateNavButtons();
        this.saveBundleProgress();
        return;
      }
      
      // Track the applied color
      this.appliedAllColor = color;
      
      // Use loaded product objects from selections.products
      const products = this.selections.products || [];
      if (!products.length) return;
      
      // Find matching variants for ALL products (including excluded)
      // so when excluded items are re-added, they sync with the current color
      products.forEach(product => {
        // Skip iPhone case products if skip toggle is enabled
        if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(product)) return;
        
        const variants = this.getMatchingVariants(product);
        // Find variant with matching color (flexible matching: "Green" matches "Glossy Green")
        const matchingVariant = variants.find(v => {
          const variantColor = v.option2 || v.option1;
          return this.colorsMatch(variantColor, color);
        });
        
        if (matchingVariant) {
          const image = matchingVariant.featured_image?.src || product.featured_image;
          const variantColor = matchingVariant.option2 || matchingVariant.option1;
          
          // Preserve device/model in title when applying color to all
          // Build title as "Device / Color" if device is set, otherwise just color
          const hasDevice = this.hasDeviceOption(product);
          const displayTitle = (hasDevice && this.selections.device) 
            ? `${this.selections.device} / ${variantColor}`
            : variantColor;
          
          this.selections.variants[product.id] = {
            variantId: matchingVariant.id,
            title: displayTitle,
            price: matchingVariant.price,
            image: image,
            color: variantColor
          };
        } else {
          // Clear old selection for products that don't have the apply-all color
          // This prevents stale colors from showing on collapsed/excluded items
          delete this.selections.variants[product.id];
        }
      });
      
      // Update UI
      this.renderProductCards();
      this.initSwatchSliders();
      this.renderApplyAllSwatches();
      this.updateApplyAllSelection();
      this.updateNavButtons();
      this.saveBundleProgress();
    },
    
    updateApplyAllSelection() {
      const selectionEl = document.querySelector('[data-apply-all-selection]');
      const colorNameEl = document.querySelector('[data-apply-all-color-name]');
      if (!selectionEl || !colorNameEl) return;
      
      if (this.appliedAllColor) {
        colorNameEl.textContent = this.appliedAllColor;
        // Apply background color matching the selected color
        const colorHex = this.getColorHex(this.appliedAllColor);
        selectionEl.style.backgroundColor = colorHex;
        selectionEl.style.display = 'flex';
        // Re-trigger animation
        selectionEl.style.animation = 'none';
        void selectionEl.offsetWidth;
        selectionEl.style.animation = '';
      } else {
        selectionEl.style.display = 'none';
        selectionEl.style.backgroundColor = '';
      }
    },
    
    updateSwatchNavVisibility(wrapper, container) {
      const hasOverflow = container.scrollWidth > container.clientWidth + 2;
      const atStart = container.scrollLeft < 5;
      const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;
      
      wrapper.classList.toggle('has-overflow', hasOverflow);
      wrapper.classList.toggle('at-start', atStart);
      wrapper.classList.toggle('at-end', atEnd);
    },
    
    getMatchingVariants(product) {
      // Guard against undefined product or variants
      if (!product || !product.variants || !Array.isArray(product.variants)) {
        return [];
      }
      
      const deviceQuery = this.selections.device?.toLowerCase().trim() || '';
      const materialQuery = this.selections.material?.toLowerCase().trim() || '';
      
      const normalizeDevice = (str) => {
        return str.toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      };
      
      // SIMPLIFIED: Products are already filtered by tag (bundle-{material}), so we trust that
      // and only need to check device compatibility for iPhone cases
      
      const normalizedDevice = normalizeDevice(deviceQuery);
      
      // Extract device model number for flexible matching (e.g., "iphone 17" from "iphone 17 air")
      const deviceModelMatch = normalizedDevice.match(/iphone\s*(\d+)/i);
      const deviceModel = deviceModelMatch ? deviceModelMatch[0] : normalizedDevice;
      
      // Check if product is a device-dependent product (iPhone case, etc.)
      const productHandle = product.handle?.toLowerCase() || '';
      const productTitle = product.title?.toLowerCase() || '';
      const deviceProductPatterns = ['iphone', 'case', 'thiki', 'θήκη', 'hülle', 'coque', 'custodia', 'funda', 'ケース', '케이스', '手机壳'];
      const isDeviceProduct = deviceProductPatterns.some(p => productHandle.includes(p) || productTitle.includes(p));
      
      // For non-device products (wallets, cardholders, etc.), return all available variants
      // Material is already validated by tag-based loading
      if (!isDeviceProduct) {
        const availableVariants = product.variants.filter(v => v.available);
        // Deduplicate by color (supports localized color option names)
        const seenColors = new Set();
        return availableVariants.filter(v => {
          const colorOption = v.options?.find((opt, idx) => {
            const option = product.options?.[idx];
            const optionName = typeof option === 'string' ? option : option?.name || '';
            return isColorOptionName(optionName);
          });
          const color = colorOption || v.option2 || v.option1;
          if (seenColors.has(color)) return false;
          seenColors.add(color);
          return true;
        });
      }
      
      // Filter variants that match the device (tolerant matching) - for device products only
      // Material is already validated by tag-based loading
      const deviceMatchedVariants = product.variants.filter(v => {
        if (!v.available) return false;
        
        const variantOptions = [
          v.option1?.toLowerCase() || '',
          v.option2?.toLowerCase() || '',
          v.option3?.toLowerCase() || ''
        ];
        
        // Exact device match, empty options ignored. A two-option case has no
        // option3, '' is contained in every string, and the containment test
        // this replaces therefore matched every variant to every device - the
        // bundle always added the first one, an iPhone 18 Pro Max, whatever
        // model the shopper picked. It also took "iPhone 16 Pro" for
        // "iPhone 16 Pro Max". A case that does not come in the chosen model
        // now shows no swatch instead of a wrong one.
        const deviceMatch = variantOptions.filter(Boolean)
          .some(opt => normalizeDevice(opt) === normalizedDevice);
        
        return deviceMatch;
      });
      
      // Deduplicate by color - only show one swatch per color (supports localized color option names)
      const seenColors = new Set();
      return deviceMatchedVariants.filter(v => {
        const colorOption = v.options.find((opt, idx) => {
          const option = product.options[idx];
          const optionName = typeof option === 'string' ? option : option?.name || '';
          return isColorOptionName(optionName);
        });
        const color = colorOption || v.option2 || v.option1;
        if (seenColors.has(color)) return false;
        seenColors.add(color);
        return true;
      });
    },
    
    renderColorSwatch(variant, product) {
      const colorOption = variant.options.find((opt, idx) => {
        const option = product.options[idx];
        const optionName = typeof option === 'string' ? option : option?.name || '';
        return isColorOptionName(optionName);
      });
      
      const color = colorOption || variant.option2 || variant.option1;
      const image = variant.featured_image?.src || product.featured_image;
      const thumbImage = this.resizeImage(image, 80);
      
      return `
        <button 
          type="button" 
          class="bundle-color-swatch bundle-color-swatch--image"
          data-variant-id="${variant.id}"
          data-color="${color}"
          data-image="${this.resizeImage(image, 160)}"
          data-price="${variant.price}"
          title="${color}">
          <img src="${thumbImage}" alt="${color}" loading="lazy" class="bundle-color-swatch__img">
        </button>
      `;
    },
    
    // Note: 'blue' maps to 'navy' for deduplication (same hex color)
    baseColors: ['black', 'navy', 'burgundy', 'green', 'brown', 'tan', 'grey', 'gray', 'white', 'red', 'pink', 'purple', 'orange', 'yellow', 'gold', 'silver', 'beige', 'magenta'],
    
    // Map localized color names to their English base color (19 languages)
    // Multi-word phrases are sorted by length automatically in normalizeColorName()
    colorAliases: {
      // Multi-word navy phrases (all languages)
      'marina militare': 'navy', 'blu marina': 'navy', 'blu notte': 'navy',  // Italian
      'bleu marine': 'navy', 'bleu nuit': 'navy',  // French
      'azul marino': 'navy', 'azul oscuro': 'navy', 'marina de guerra': 'navy', 'armada': 'navy',  // Spanish
      'azul marinho': 'navy', 'azul escuro': 'navy',  // Portuguese
      'marineblau': 'navy', 'dunkelblau': 'navy',  // German
      'σκούρο μπλε': 'navy', 'θαλασσί': 'navy',  // Greek
      'ネイビー ブルー': 'navy', '海軍藍': 'navy', '藏青色': 'navy',  // Japanese/Chinese
      'กรมท่า': 'navy', 'สีกรมท่า': 'navy',  // Thai
      'темно синий': 'navy', 'темно-синий': 'navy',  // Russian
      'granatowy': 'navy', 'ciemny niebieski': 'navy',  // Polish
      'koyu mavi': 'navy', 'lacivert': 'navy',  // Turkish
      'أزرق داكن': 'navy', 'أزرق بحري': 'navy',  // Arabic
      'כחול כהה': 'navy',  // Hebrew
      'गहरा नीला': 'navy',  // Hindi
      // Multi-word dark/light color phrases
      'gris oscuro': 'gray', 'gris claro': 'gray',  // Spanish
      'grigio scuro': 'gray', 'grigio chiaro': 'gray',  // Italian
      'rosa chiaro': 'pink', 'rosa scuro': 'pink',  // Italian  
      'verde scuro': 'green', 'verde chiaro': 'green',  // Italian
      'marrone scuro': 'brown', 'marrone chiaro': 'brown',  // Italian
      'vert foncé': 'green', 'vert clair': 'green',  // French
      'rose clair': 'pink', 'rose foncé': 'pink',  // French
      'gris foncé': 'gray', 'gris clair': 'gray',  // French
      'dunkelgrün': 'green', 'hellgrün': 'green',  // German
      'dunkelbraun': 'brown', 'hellbraun': 'brown',  // German
      'dunkelgrau': 'gray', 'hellgrau': 'gray',  // German
      // English - map blue to navy for deduplication
      'navy blue': 'navy', 'dark blue': 'navy',
      'blue': 'navy',
      // Spanish
      'negro': 'black', 'azul': 'navy', 'burdeos': 'burgundy', 'verde': 'green',
      'marrón': 'brown', 'bronceado': 'tan', 'gris': 'gray', 'blanco': 'white',
      'rojo': 'red', 'rosa': 'pink', 'morado': 'purple', 'naranja': 'orange',
      'amarillo': 'yellow', 'dorado': 'gold', 'plateado': 'silver', 'magenta': 'magenta',
      // German
      'schwarz': 'black', 'blau': 'navy', 'grün': 'green', 'braun': 'brown',
      'grau': 'gray', 'weiß': 'white', 'rot': 'red', 'gelb': 'yellow', 'silber': 'silver',
      // French
      'noir': 'black', 'bleu': 'navy', 'bordeaux': 'burgundy', 'vert': 'green',
      'marron': 'brown', 'blanc': 'white', 'rouge': 'red', 'rose': 'pink',
      'violet': 'purple', 'jaune': 'yellow', 'or': 'gold', 'argent': 'silver',
      // Italian (expanded with all colors)
      'nero': 'black', 'blu': 'navy', 'borgogna': 'burgundy', 'marrone': 'brown',
      'grigio': 'gray', 'bianco': 'white', 'rosso': 'red', 'viola': 'purple',
      'arancione': 'orange', 'giallo': 'yellow', 'oro': 'gold', 'argento': 'silver',
      'verde': 'green', 'rosa': 'pink', 'porpora': 'purple', 'cuoio': 'tan',
      'crema': 'beige', 'avorio': 'beige', 'magenta': 'magenta',
      // Japanese (multiple variants for each color)
      '黒': 'black', 'ブラック': 'black', 'クロ': 'black',
      '紺': 'navy', 'ネイビー': 'navy', '海軍': 'navy', 'ネービー': 'navy', '紺色': 'navy',
      'バーガンディ': 'burgundy', 'バーガンディー': 'burgundy', 'ブルゴーニュ': 'burgundy', 'ワインレッド': 'burgundy',
      '緑': 'green', 'グリーン': 'green', '緑色': 'green', 'みどり': 'green',
      '茶': 'brown', 'ブラウン': 'brown', '茶色': 'brown', 'チャイロ': 'brown',
      'タン': 'tan', '灰': 'gray', 'グレー': 'gray', 'グレイ': 'gray', '灰色': 'gray',
      '白': 'white', 'ホワイト': 'white', '白色': 'white', 'しろ': 'white',
      '赤': 'red', 'レッド': 'red', '赤色': 'red', 'あか': 'red',
      'ピンク': 'pink', '桃色': 'pink', 'ももいろ': 'pink',
      '紫': 'purple', 'パープル': 'purple', '紫色': 'purple', 'むらさき': 'purple',
      'オレンジ': 'orange', '橙': 'orange', 'だいだい': 'orange', 'オレンジ色': 'orange',
      '黄': 'yellow', 'イエロー': 'yellow', '黄色': 'yellow', 'きいろ': 'yellow',
      '金': 'gold', 'ゴールド': 'gold', '金色': 'gold', 'きん': 'gold',
      '銀': 'silver', 'シルバー': 'silver', '銀色': 'silver', 'ぎん': 'silver',
      'ベージュ': 'beige', 'マゼンタ': 'magenta', '青': 'navy', 'ブルー': 'navy', '青色': 'navy',
      // Korean (expanded with more variants)
      '검정': 'black', '블랙': 'black', '검정색': 'black', '흑색': 'black', '검은색': 'black',
      '네이비': 'navy', '해군': 'navy', '남색': 'navy', '네비': 'navy', '진청': 'navy',
      '버건디': 'burgundy', '부르고뉴': 'burgundy', '와인': 'burgundy', '적갈색': 'burgundy',
      '녹색': 'green', '그린': 'green', '초록': 'green', '초록색': 'green',
      '갈색': 'brown', '브라운': 'brown', '밤색': 'brown', '갈색': 'brown',
      '회색': 'gray', '그레이': 'gray', '회': 'gray', '잿빛': 'gray',
      '흰색': 'white', '화이트': 'white', '백색': 'white', '하양': 'white',
      '빨강': 'red', '레드': 'red', '빨간색': 'red', '적색': 'red',
      '핑크': 'pink', '분홍': 'pink', '분홍색': 'pink',
      '보라': 'purple', '퍼플': 'purple', '보라색': 'purple', '자주': 'purple',
      '오렌지': 'orange', '주황': 'orange', '주황색': 'orange',
      '노랑': 'yellow', '옐로우': 'yellow', '노란색': 'yellow', '황색': 'yellow',
      '금색': 'gold', '골드': 'gold', '금': 'gold',
      '은색': 'silver', '실버': 'silver', '은': 'silver',
      '베이지': 'beige', '마젠타': 'magenta', '파랑': 'navy', '파란색': 'navy', '블루': 'navy',
      // Chinese (Simplified)
      '黑': 'black', '黑色': 'black', '海军蓝': 'navy', '藏青': 'navy', '酒红': 'burgundy',
      '绿色': 'green', '棕': 'brown', '棕色': 'brown', '灰色': 'gray', '白色': 'white',
      '红': 'red', '红色': 'red', '粉': 'pink', '粉色': 'pink', '紫色': 'purple',
      '橙': 'orange', '橙色': 'orange', '黄色': 'yellow', '金色': 'gold', '银色': 'silver',
      '米色': 'beige', '洋红': 'magenta',
      // Portuguese
      'preto': 'black', 'azul marinho': 'navy', 'bordô': 'burgundy', 'castanho': 'brown',
      'cinzento': 'gray', 'cinza': 'gray', 'branco': 'white', 'vermelho': 'red',
      'roxo': 'purple', 'laranja': 'orange', 'amarelo': 'yellow', 'dourado': 'gold', 'prateado': 'silver',
      // Dutch
      'zwart': 'black', 'marineblauw': 'navy', 'groen': 'green', 'bruin': 'brown',
      'grijs': 'gray', 'wit': 'white', 'rood': 'red', 'roze': 'pink', 'paars': 'purple',
      'oranje': 'orange', 'geel': 'yellow', 'goud': 'gold', 'zilver': 'silver',
      // Swedish/Norwegian/Danish
      'svart': 'black', 'marinblå': 'navy', 'grønn': 'green', 'grön': 'green',
      'brun': 'brown', 'grå': 'gray', 'vit': 'white', 'hvit': 'white', 'hvid': 'white',
      'röd': 'red', 'rød': 'red', 'lila': 'purple', 'gul': 'yellow', 'guld': 'gold', 'sølv': 'silver',
      // Finnish
      'musta': 'black', 'laivastonsininen': 'navy', 'vihreä': 'green', 'ruskea': 'brown',
      'harmaa': 'gray', 'valkoinen': 'white', 'punainen': 'red', 'vaaleanpunainen': 'pink',
      'violetti': 'purple', 'oranssi': 'orange', 'keltainen': 'yellow', 'kulta': 'gold', 'hopea': 'silver',
      // Arabic
      'أسود': 'black', 'كحلي': 'navy', 'أخضر': 'green', 'بني': 'brown', 'رمادي': 'gray',
      'أبيض': 'white', 'أحمر': 'red', 'وردي': 'pink', 'بنفسجي': 'purple', 'برتقالي': 'orange',
      'أصفر': 'yellow', 'ذهبي': 'gold', 'فضي': 'silver', 'بيج': 'beige',
      // Hebrew
      'שחור': 'black', 'כחול': 'navy', 'ירוק': 'green', 'חום': 'brown', 'אפור': 'gray',
      'לבן': 'white', 'אדום': 'red', 'ורוד': 'pink', 'סגול': 'purple', 'כתום': 'orange',
      'צהוב': 'yellow', 'זהב': 'gold', 'כסף': 'silver',
      // Greek (expanded with more variants)
      'μαύρο': 'black', 'μαύρος': 'black', 'μαυρος': 'black',
      'ναυτικό': 'navy', 'μπλε': 'navy', 'μπλέ': 'navy',
      'πράσινο': 'green', 'πρασινο': 'green',
      'καφέ': 'brown', 'καστανός': 'brown', 'καστανος': 'brown', 'καστανό': 'brown',
      'γκρι': 'gray', 'γκρί': 'gray',
      'λευκό': 'white', 'λευκος': 'white', 'άσπρο': 'white',
      'κόκκινο': 'red', 'κοκκινο': 'red',
      'ροζ': 'pink', 'ρόζ': 'pink',
      'μοβ': 'purple', 'βιολετί': 'purple',
      'πορτοκαλί': 'orange', 'πορτοκαλι': 'orange',
      'κίτρινο': 'yellow', 'κιτρινο': 'yellow',
      'χρυσό': 'gold', 'χρυσος': 'gold',
      'ασημί': 'silver', 'ασημι': 'silver',
      'βουργουνδία': 'burgundy', 'μπορντό': 'burgundy', 'μπορντο': 'burgundy', 'βυσσινί': 'burgundy',
      'ματζέντα': 'magenta', 'ματζεντα': 'magenta', 'φούξια': 'magenta',
      'μπεζ': 'beige', 'μπέζ': 'beige',
      // Hindi (expanded with more variants)
      'काला': 'black', 'ब्लैक': 'black', 'कालापन': 'black',
      'नेवी': 'navy', 'नौसेना': 'navy', 'नीला': 'navy', 'नील': 'navy', 'गहरा नीला': 'navy',
      'बरगंडी': 'burgundy', 'बर्गंडी': 'burgundy', 'मैरून': 'burgundy', 'गहरा लाल': 'burgundy',
      'हरा': 'green', 'ग्रीन': 'green', 'हरापन': 'green', 'हरी': 'green',
      'भूरा': 'brown', 'ब्राउन': 'brown', 'कत्थई': 'brown',
      'धूसर': 'gray', 'ग्रे': 'gray', 'सलेटी': 'gray', 'भूरापन': 'gray',
      'सफेद': 'white', 'व्हाइट': 'white', 'श्वेत': 'white',
      'लाल': 'red', 'रेड': 'red', 'सुर्ख': 'red',
      'गुलाबी': 'pink', 'पिंक': 'pink', 'गुलाब': 'pink',
      'बैंगनी': 'purple', 'पर्पल': 'purple', 'जामुनी': 'purple',
      'नारंगी': 'orange', 'ऑरेंज': 'orange', 'संतरी': 'orange',
      'पीला': 'yellow', 'येलो': 'yellow', 'पीलापन': 'yellow',
      'सोना': 'gold', 'गोल्ड': 'gold', 'सुनहरा': 'gold', 'स्वर्ण': 'gold',
      'चांदी': 'silver', 'सिल्वर': 'silver', 'रजत': 'silver',
      'मैजेंटा': 'magenta', 'गुलाबी बैंगनी': 'magenta',
      // Tamil
      'கருப்பு': 'black', 'பச்சை': 'green', 'பழுப்பு': 'brown', 'சாம்பல்': 'gray',
      'வெள்ளை': 'white', 'சிவப்பு': 'red', 'இளஞ்சிவப்பு': 'pink', 'ஊதா': 'purple',
      'ஆரஞ்சு': 'orange', 'மஞ்சள்': 'yellow', 'தங்கம்': 'gold', 'வெள்ளி': 'silver',
      // Common variants
      'beige': 'beige', 'tan': 'tan'
    },
    
    normalizeColorName(colorName) {
      if (!colorName) return '';
      let lower = colorName.toLowerCase().trim();
      // Strip Latin color modifiers/adjectives (expanded for all European languages)
      // English: glossy, matte, shiny, dark, light
      // Spanish: brillante, mate, oscuro, claro
      // German: glänzend, matt, dunkel, hell
      // French: brillant, mat, sombre, clair
      // Italian: lucido, opaco, scuro, chiaro
      // Portuguese: brilhante, fosco, escuro, claro
      // Dutch: glanzend, mat, donker, licht
      // Swedish/Norwegian/Danish: blank, matt, mörk, ljus, mørk, lys
      // Finnish: kiiltävä, matta, tumma, vaalea
      // Greek: γυαλιστερό, ματ, σκούρο, ανοιχτό
      lower = lower.replace(/\b(glossy|matte?|shiny|dark|light|brillant[es]?|lucid[oa]?|opac[oa]?|mate|satinat[oa]?|glänzend|dunkel|hell|oscur[oa]?|clar[oa]?|sombres?|clair[es]?|scur[oa]?|chiar[oa]?|brilhante|fosco|escur[oa]?|glanzend|donker|licht|blank|mörk|ljus|mørk|lys|kiiltävä|matta|tumma|vaalea|γυαλιστερό|ματ|σκούρο|ανοιχτό)\b/gi, '').trim();
      // Strip CJK and Indic modifiers (Korean, Japanese, Chinese, Hindi, Tamil, Arabic, Hebrew)
      // Korean: 광택(glossy), 무광(matte), 반짝이(shiny), 어두운(dark), 밝은(light), 메탈릭(metallic)
      // Japanese: 光沢(glossy), ツヤ(gloss), マット(matte), 艶(luster)
      // Chinese: 光泽(glossy), 哑光(matte), 亮(bright)
      // Hindi: चमकदार(glossy), मैट(matte), चमकीला(shiny), गहरा(dark), हल्का(light)
      // Arabic: لامع(glossy), غير لامع(matte)
      lower = lower.replace(/(광택|무광|반짝이|어두운|밝은|메탈릭|光沢|ツヤ|マット|艶|艶消し|光泽|哑光|亮|चमकदार|मैट|चमकीला|गहरा|हल्का|لامع|غير لامع)/g, '').trim();
      
      // Sort aliases by length (longest first) to prevent substring false positives
      // e.g. "검은색" should match before "은" does
      const sortedAliases = Object.entries(this.colorAliases)
        .sort((a, b) => b[0].length - a[0].length);
      
      // Check for localized color alias (longest match first)
      for (const [alias, base] of sortedAliases) {
        if (lower.includes(alias)) return base;
      }
      // Then check base colors
      for (const base of this.baseColors) {
        if (lower === base || lower.includes(base)) return base;
      }
      return lower;
    },
    
    colorsMatch(color1, color2) {
      return this.normalizeColorName(color1) === this.normalizeColorName(color2);
    },
    
    getColorHex(colorName) {
      const colors = {
        'black': '#1a1a1a',
        'navy': '#1a2744',
        'navy blue': '#1a2744',
        'burgundy': '#722f37',
        'green': '#2d4a3e',
        'dark green': '#1a3a2a',
        'brown': '#5c4033',
        'tan': '#d2b48c',
        'grey': '#808080',
        'gray': '#808080',
        'white': '#f5f5f5',
        'red': '#8b0000',
        'blue': '#2563eb',
        'pink': '#ec4899',
        'purple': '#7c3aed',
        'orange': '#ea580c',
        'yellow': '#eab308',
        'gold': '#d4af37',
        'silver': '#c0c0c0',
        'beige': '#f5f5dc',
        'magenta': '#b5338a'
      };
      
      const normalized = this.normalizeColorName(colorName);
      return colors[normalized] || '#888888';
    },
    
    resizeImage(url, size) {
      if (!url) return '';
      if (url.includes('_')) {
        return url.replace(/(_\d+x\d+)/, `_${size}x${size}`);
      }
      const parts = url.split('.');
      const ext = parts.pop();
      return `${parts.join('.')}_${size}x${size}.${ext}`;
    },
    
    renderSummary() {
      const container = document.querySelector('[data-summary-items]');
      const totalEl = document.querySelector('[data-bundle-total]');
      const savingsEl = document.querySelector('[data-bundle-savings]');
      
      if (!container) return;
      
      container.innerHTML = '';
      let total = 0;
      // The bundle discount is 20% off 3 items or more. Quoting it on one or
      // two items promised a price checkout never charged. Counted the way
      // addBundleToCart counts what it sends.
      const gcCount = this.selections.products.filter(p => !this.isExcluded(p.id)
        && !(this.selections.skipIphoneCase && this.isIphoneCaseProduct(p))
        && this.selections.variants[p.id]?.variantId).length;
      const gcRate = gcCount >= 3 ? 0.2 : 0;
      
      for (const product of this.selections.products) {
        // Skip excluded products
        if (this.isExcluded(product.id)) continue;
        // Skip iPhone case products if skip toggle is enabled
        if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(product)) continue;
        
        const selection = this.selections.variants[product.id];
        if (!selection) continue;
        
        const price = parseInt(selection.price) / 100;
        total += parseInt(selection.price);
        
        const discountedPrice = Math.round(parseInt(selection.price) * (1 - gcRate));
        container.innerHTML += `
          <div class="bundle-summary-item">
            <img class="bundle-summary-item__image" src="${selection.image}" alt="${product.title}" loading="lazy">
            <div class="bundle-summary-item__info">
              <h4 class="bundle-summary-item__title">${product.title}</h4>
              <p class="bundle-summary-item__variant">${selection.title || selection.color}</p>
            </div>
            <div class="bundle-summary-item__prices">
              <span class="bundle-summary-item__price">${this.formatMoney(discountedPrice)}</span>
              ${gcRate ? `<span class="bundle-summary-item__compare-price">${this.formatMoney(selection.price)}</span>` : ''}
            </div>
          </div>
        `;
      }
      
      const savings = Math.round(total * gcRate);
      const finalTotal = total - savings;
      
      if (savingsEl) {
        // Under three items the row says how far away the discount is rather
        // than quoting a saving that will not arrive.
        const gcLabel = savingsEl.parentElement?.querySelector('.bundle-summary__savings-label');
        if (gcLabel && gcLabel.dataset.gcText === undefined) gcLabel.dataset.gcText = gcLabel.textContent;
        if (gcRate) {
          if (gcLabel) gcLabel.textContent = gcLabel.dataset.gcText;
          savingsEl.style.display = '';
          savingsEl.textContent = `-${this.formatMoney(savings)}`;
        } else {
          if (gcLabel) gcLabel.textContent = (window.bundleTranslations?.addMoreToSave || 'Add {n} more to save 20%')
            .replace('{n}', String(3 - gcCount));
          savingsEl.style.display = 'none';
          savingsEl.textContent = '';
        }
      }
      
      if (totalEl) {
        totalEl.textContent = this.formatMoney(finalTotal);
      }
    },
    
    formatMoney(cents) {
      const amount = parseInt(cents) / 100;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        /* whole dollars print as "$89", never "$89.00"; a 20%-off line such as $71.20 keeps its cents
           (owner, 17 Oct 2026). Both limits are set: a minimum alone would print "$71.2". */
        ...(parseInt(cents) % 100 === 0 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {})
      }).format(amount);
    },
    
    async addBundleToCart() {
      const nextBtn = document.querySelector('[data-action="bundle-next"]');
      const nextText = nextBtn?.querySelector('[data-next-text]');
      
      if (nextBtn) {
        nextBtn.disabled = true;
      }
      if (nextText) {
        nextText.textContent = window.bundleTranslations?.adding || 'Adding...';
      }
      
      try {
        const items = [];
        
        for (const product of this.selections.products) {
          // Skip excluded products
          if (this.isExcluded(product.id)) continue;
          // Skip iPhone case products if skip toggle is enabled
          if (this.selections.skipIphoneCase && this.isIphoneCaseProduct(product)) continue;
          
          const selection = this.selections.variants[product.id];
          if (selection?.variantId) {
            items.push({
              id: selection.variantId,
              quantity: 1,
              properties: {
                '_bundle': 'true',
                '_bundle_discount': '20%'
              }
            });
          }
        }
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });
        
        if (response.ok) {
          if (nextText) {
            nextText.textContent = window.bundleTranslations?.added || 'Added!';
          }
          
          // Clear saved bundle progress after successful add to cart
          sessionStorage.removeItem('bundleProgress');
          this.hideFloatingBundleButton();
          
          setTimeout(async () => {
            this.close();
            
            // Dispatch product:added event - theme will handle cart drawer refresh
            document.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                variant: null
              }
            }));
            
            // Also trigger cart:refresh for any custom listeners
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Fetch fresh cart data and update sections via Section Rendering API
            const rootUrl = (window.Shopify?.routes?.root) || '/';
            try {
              const sectionResponse = await fetch(`${rootUrl}?sections=mini-cart`);
              if (sectionResponse.ok) {
                const sections = await sectionResponse.json();
                const miniCartHtml = sections['mini-cart'];
                if (miniCartHtml) {
                  // Find the cart drawer section and replace its entire content
                  const cartSection = document.getElementById('shopify-section-mini-cart');
                  if (cartSection) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(miniCartHtml, 'text/html');
                    const newSection = doc.getElementById('shopify-section-mini-cart');
                    if (newSection) {
                      cartSection.innerHTML = newSection.innerHTML;
                    }
                  }
                  
                  // Also try updating the sidebar-cart directly
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(miniCartHtml, 'text/html');
                  
                  // Update cart items
                  const newCartMain = doc.querySelector('.Drawer__Main');
                  const currentCartMain = document.querySelector('#sidebar-cart .Drawer__Main');
                  if (newCartMain && currentCartMain) {
                    currentCartMain.innerHTML = newCartMain.innerHTML;
                  }
                  
                  // Update footer/totals
                  const newFooter = doc.querySelector('.Drawer__Footer');
                  const currentFooter = document.querySelector('#sidebar-cart .Drawer__Footer');
                  if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                  }
                }
              }
            } catch (e) {
              console.error('Error refreshing cart section:', e);
            }
            
            // Update cart count
            try {
              const cartResponse = await fetch('/cart.js');
              if (cartResponse.ok) {
                const cart = await cartResponse.json();
                const countEls = document.querySelectorAll('.Header__CartCount, [data-cart-count], #CartCountValue, #CartCountValueDrawer');
                countEls.forEach(el => {
                  el.textContent = cart.item_count;
                  if (el.id === 'CartCountValue' || el.id === 'CartCountValueDrawer') {
                    el.style.display = cart.item_count > 0 ? 'flex' : 'none';
                  }
                });
              }
            } catch (e) {
              console.error('Error updating cart count:', e);
            }
            
            // Open cart drawer
            const cartDrawer = document.querySelector('#sidebar-cart');
            if (cartDrawer) {
              cartDrawer.setAttribute('aria-hidden', 'false');
              document.body.classList.add('has-overlay');
            }
            
            // Reinitialize cart upsell after a short delay
            setTimeout(() => {
              if (typeof window.reinitCartUpsell === 'function') {
                window.reinitCartUpsell();
              }
            }, 100);
              
          }, 800);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (err) {
        console.error('Error adding bundle to cart:', err);
        if (nextBtn) {
          nextBtn.disabled = false;
        }
        if (nextText) {
          nextText.textContent = window.bundleTranslations?.addBundle || 'Add Bundle to Cart';
        }
      }
    },
    
    saveBundleProgress() {
      try {
        const progress = {
          device: this.selections.device,
          material: this.selections.material,
          productHandle: this.currentProductHandle,
          bundleProducts: this.bundleProducts,
          currentStep: this.currentStep,
          variants: this.selections.variants,
          excluded: Array.from(this.selections.excluded),
          skipIphoneCase: this.selections.skipIphoneCase || false,
          timestamp: Date.now()
        };
        sessionStorage.setItem('bundleProgress', JSON.stringify(progress));
        this.showFloatingBundleButton();
      } catch (e) {
        console.error('Error saving bundle progress:', e);
      }
    },
    
    showFloatingBundleButton() {
      console.log('[Bundle] showFloatingBundleButton called');
      let btn = document.querySelector('[data-resume-bundle]');
      if (!btn) {
        console.log('[Bundle] Creating new floating button');
        btn = document.createElement('div');
        btn.className = 'floating-bundle-btn';
        btn.setAttribute('data-resume-bundle', 'true');
        btn.innerHTML = `
          <div class="floating-bundle-btn__content" data-bundle-resume-trigger>
            <span class="floating-bundle-btn__icon">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </span>
            <span>${window.bundleTranslations?.floatingButtonText || 'Bundle'}</span>
          </div>
          <button type="button" class="floating-bundle-btn__dismiss" data-dismiss-bundle aria-label="Dismiss"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button>
        `;
        document.body.appendChild(btn);
        this.initFloatingButtonDrag(btn);
      }
      btn.style.display = 'flex';
      btn.classList.remove('dismissed');
      console.log('[Bundle] Floating button displayed:', btn.style.display, 'visibility:', getComputedStyle(btn).visibility);
    },
    
    hideFloatingBundleButton() {
      const btn = document.querySelector('[data-resume-bundle]');
      if (btn) btn.style.display = 'none';
    },
    
    dismissFloatingButton() {
      const btn = document.querySelector('[data-resume-bundle]');
      if (btn) {
        btn.style.display = 'none';
        btn.classList.add('dismissed');
        sessionStorage.setItem('bundleDismissed', 'true');
      }
    },
    
    initFloatingButtonDrag(btn) {
      let isDragging = false;
      let hasMoved = false;
      let startX, startY, initialX, initialY;
      
      const onStart = (e) => {
        if (e.target.closest('[data-dismiss-bundle]')) {
          return;
        }
        isDragging = true;
        hasMoved = false;
        const rect = btn.getBoundingClientRect();
        initialX = rect.left;
        initialY = rect.top;
        
        if (e.type === 'touchstart') {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        } else {
          startX = e.clientX;
          startY = e.clientY;
        }
        btn.style.transition = 'none';
      };
      
      const onMove = (e) => {
        if (!isDragging) return;
        
        let currentX, currentY;
        if (e.type === 'touchmove') {
          currentX = e.touches[0].clientX;
          currentY = e.touches[0].clientY;
        } else {
          currentX = e.clientX;
          currentY = e.clientY;
        }
        
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          hasMoved = true;
          e.preventDefault();
        }
        
        let newX = initialX + deltaX;
        let newY = initialY + deltaY;
        
        const maxX = window.innerWidth - btn.offsetWidth - 10;
        const maxY = window.innerHeight - btn.offsetHeight - 10;
        newX = Math.max(10, Math.min(newX, maxX));
        newY = Math.max(10, Math.min(newY, maxY));
        
        btn.style.left = newX + 'px';
        btn.style.top = newY + 'px';
        btn.style.right = 'auto';
        btn.style.bottom = 'auto';
      };
      
      const onEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        btn.style.transition = '';
        
        sessionStorage.setItem('bundleBtnPos', JSON.stringify({
          left: btn.style.left,
          top: btn.style.top
        }));
        
        if (!hasMoved && !e.target.closest('[data-dismiss-bundle]')) {
          this.resumeBundleProgress();
        }
      };
      
      btn.addEventListener('mousedown', onStart);
      btn.addEventListener('touchstart', onStart, { passive: false });
      document.addEventListener('mousemove', onMove);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('mouseup', onEnd.bind(this));
      document.addEventListener('touchend', onEnd.bind(this));
      
      const savedPos = sessionStorage.getItem('bundleBtnPos');
      if (savedPos) {
        try {
          const pos = JSON.parse(savedPos);
          const left = parseInt(pos.left, 10);
          const top = parseInt(pos.top, 10);
          const btnWidth = btn.offsetWidth || 120;
          const btnHeight = btn.offsetHeight || 50;
          
          // Validate position is within viewport
          const isValid = !isNaN(left) && !isNaN(top) &&
            left >= 0 && left <= window.innerWidth - btnWidth &&
            top >= 0 && top <= window.innerHeight - btnHeight;
          
          if (isValid) {
            btn.style.left = pos.left;
            btn.style.top = pos.top;
            btn.style.right = 'auto';
            btn.style.bottom = 'auto';
          } else {
            // Clear invalid saved position - use CSS defaults (bottom: 100px, right: 20px)
            sessionStorage.removeItem('bundleBtnPos');
          }
        } catch (e) {
          sessionStorage.removeItem('bundleBtnPos');
        }
      }
    },
    
    resumeBundleProgress() {
      try {
        const saved = sessionStorage.getItem('bundleProgress');
        if (saved) {
          const progress = JSON.parse(saved);
          // Check if saved less than 30 minutes ago
          if (Date.now() - progress.timestamp < 30 * 60 * 1000) {
            this.resumeFromSavedState(progress);
          } else {
            sessionStorage.removeItem('bundleProgress');
            this.hideFloatingBundleButton();
          }
        }
      } catch (e) {
        console.error('Error resuming bundle progress:', e);
      }
    },
    
    async resumeFromSavedState(progress) {
      try {
        this.bundleProducts = progress.bundleProducts;
        this.currentProductHandle = progress.productHandle;
        
        // Restore selections BEFORE loading products to prevent duplication
        this.selections.device = progress.device;
        this.selections.material = progress.material;
        this.selections.variants = progress.variants || {};
        this.selections.excluded = new Set(progress.excluded || []);
        this.selections.skipIphoneCase = progress.skipIphoneCase || false;
        this.currentStep = progress.currentStep || 1;
        
        // Clear existing products before loading
        this.selections.products = [];
        
        // Populate device options first
        this.populateDeviceOptions();
        
        // Open drawer
        this.drawer.setAttribute('aria-hidden', 'false');
        this.overlay.style.opacity = '1';
        this.overlay.style.visibility = 'visible';
        this.overlay.style.pointerEvents = 'auto';
        document.body.style.overflow = 'hidden';
        
        await this.loadBundleProducts();
        
        // Update UI to reflect restored state
        if (progress.device) {
          const deviceBtn = document.querySelector(`.bundle-device-option[data-device="${progress.device}"]`);
          if (deviceBtn) deviceBtn.classList.add('selected');
        }
        
        if (progress.material) {
          const materialBtn = document.querySelector(`.bundle-material-option[data-material="${progress.material}"]`);
          if (materialBtn) {
            materialBtn.classList.add('selected');
            materialBtn.classList.add('bundle-material-option--highlighted');
          }
        }
        
        // Render current step
        if (this.currentStep === 3) {
          this.renderProductCards();
          this.initSwatchSliders();
        } else if (this.currentStep === 4) {
          this.renderSummary();
        }
        
        this.updateStepDisplay();
        this.updateProgress();
        this.updateNavButtons();
        
        document.body.classList.add('bundle-drawer-open');
        this.hideFloatingBundleButton();
      } catch (err) {
        console.error('Error resuming from saved state:', err);
        // Fallback to normal open
        this.open(progress.bundleProducts, progress.productHandle);
      }
    },
    
    checkSavedProgress() {
      // Note: Floating button is NOT shown on page load.
      // It only appears after user clicks the bundle pill (controlled by bundlePillClickedThisPage flag)
      // This function is intentionally a no-op on initial page load.
      // The floating button is shown via saveBundleProgress() when user closes drawer with progress.
    }
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      BundleDrawer.init();
      BundleDrawer.checkSavedProgress();
    });
  } else {
    BundleDrawer.init();
    BundleDrawer.checkSavedProgress();
  }
  
  window.BundleDrawer = BundleDrawer;
})();
